import { useRef } from 'react';

import type { VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import type { ColorVariant } from '@/theme/tokens';

import { Button, buttonVariants } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import Text from './text';

type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>['variant']
>;

const confirmVariantMap: Record<ColorVariant, ButtonVariant> = {
  primary: 'default',
  neutral: 'neutral',
  error: 'destructive',
  success: 'success',
  warning: 'warning',
  info: 'info',
  teal: 'teal',
  muted: 'secondary',
};

export type ConfirmDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  itemName?: string;
  isLoading?: boolean;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: ColorVariant;
  className?: string;
};

function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm',
  description,
  itemName,
  isLoading = false,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmVariant = 'primary',
  className,
}: Readonly<ConfirmDialogProps>) {
  const stable = useRef({ title, description, itemName });
  if (isOpen) {
    stable.current = { title, description, itemName };
  }
  const {
    title: stableTitle,
    description: stableDescription,
    itemName: stableItemName,
  } = stable.current;

  const renderDescription = () => {
    if (stableDescription) {
      return <Text>{stableDescription}</Text>;
    }
    if (stableItemName) {
      return (
        <Text>
          Are you sure you want to {confirmText.toLowerCase()}{' '}
          <strong>{stableItemName}</strong>?
        </Text>
      );
    }
    return <Text>Are you sure you want to proceed?</Text>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={cn(className)}>
        <DialogHeader>
          <DialogTitle>{stableTitle}</DialogTitle>
        </DialogHeader>
        {renderDescription()}
        <div className="flex flex-row justify-end gap-5 sm:gap-4">
          <Button
            type="button"
            variant="link"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            type="button"
            variant={confirmVariantMap[confirmVariant]}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {confirmText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { ConfirmDialog };
export default ConfirmDialog;
